import classNames from "classnames";
import React from "react";
import {
  SanityImage,
  SanityMediaGrid,
  SanityMediaPair,
  SanitySimpleContentObject,
  SanityYoutubeVideo
} from "../../model/common";
import BlockContent from "../BlockContent/BlockContent";
import { Col, Row } from "../Grid";
import ImageBlock from "../ImageBlock/ImageBlock";
import Text from "../Text/Text";
import YoutubeVideo from "../YoutubeVideoBlock/YoutubeVideoBlock";
import styles from "./MediaPair.module.scss";

interface MediaPairProps {
  node: SanityMediaPair;
}

export const Media = ({
  className,
  value
}: {
  className?: string;
  value: SanityImage | SanityYoutubeVideo;
}) => {
  switch (value._type) {
    case "youtubeVideo":
      return (
        <YoutubeVideo
          className={className}
          node={value as SanityYoutubeVideo}
        />
      );
    case "simpleImage":
      return <ImageBlock className={className} node={value as SanityImage} />;
    default:
      throw new Error(`Unknown media ${value._type}`);
  }
};

export const MediaCol = ({
  title,
  media,
  content,
  cols
}: {
  title?: string;
  media: SanityYoutubeVideo | SanityImage;
  content: SanitySimpleContentObject | undefined;
  cols: number;
}) => (
  <Col md={cols} sm={12} xs={12}>
    {title && <Text variant="h3">{title}</Text>}
    {media && <Media className={title && styles.removeMargin} value={media} />}
    {content?.content?.length > 0 && (
      <BlockContent className={styles.content} content={content?.content} />
    )}
  </Col>
);

interface MediaGridProps {
  node: SanityMediaGrid;
  className?: string;
}

export const MediaGrid = ({
  node: { items = [], columns = 2 },
  className
}: MediaGridProps) => {
  const rows: JSX.Element[] = [];
  let cols: JSX.Element[] = [];
  items.forEach(item => {
    if (cols.length === columns) {
      rows.push(
        <Row
          className={classNames(styles.root, className)}
          key={item.media._id}
        >
          {cols}
        </Row>
      );
      cols = [];
    }
    cols.push(
      <MediaCol
        cols={12 / columns}
        key={item._key}
        title={item.title}
        media={item.media}
        content={item.content}
      />
    );
  });
  if (cols.length > 0) {
    rows.push(
      <Row className={classNames(styles.root, className)} key="final">
        {cols}
      </Row>
    );
  }
  return <>{rows}</>;
};

const MediaPair = ({
  node: {
    leftTitle,
    rightTitle,
    leftMedia,
    rightMedia,
    leftContent,
    rightContent,
    componentWidth = "full"
  }
}: MediaPairProps) => (
  <MediaGrid
    className={`${componentWidth}Width`}
    node={{
      columns: 2,
      items: [
        {
          _key: "left",
          title: leftTitle,
          media: leftMedia,
          content: leftContent
        },
        {
          _key: "right",
          title: rightTitle,
          media: rightMedia,
          content: rightContent
        }
      ]
    }}
  />
);

export default MediaPair;
